/* global dataLayer */
/* eslint no-unused-vars: off */

'use strict';

import Cookie from 'js-cookie';

const results = {};

/**
 * Creates new A/B test which sets a cookie, pushes data layer to GA and optionally adds CSS class to html element.
 * All the test results are stored in a variable called 'results'.
 *
 * @example
 *   test({
 *     'exampleTestName': [
 *       {
 *         value: 'true',
 *         cssClass: 'ab-test-show-example-test-class'
 *       },
 *       {
 *         value: 'false',
 *         cssClass: ''
 *       }
 *     ]
 *   });
 *
 * @param {Object} config
 *        {String} config.[].{}.value
 *        {String} [config.[].{}.cssClass]
 *        {String} [config.dataLayerVar]
 * @param {Number} [percentageOfUsers] - Controls how many users will get the first variant (e.g. 0 = nobody, 50 = half, 100 = all).
 *                                       When used, there has to be exactly 2 variants.
 *                                       Default percentage is equal between the variants (e.q. 2 variants = 50%, 3 variants = 33% etc.).
 * @param {Number} expires - Cookie time to live in days
 *
 * @return {Object}
 *         {String} name - Name of the test
 *         {Object} variant - Current variant data
 *         {Boolean} isNewUser - Was it a new or a returning user
 */

function test(config, percentageOfUsers, expires) {
	const name = Object.keys(config)[0];
	const variants = config[name];
	const cookieName = `abTest_${name}`;
	const currentCookie = String(Cookie.get(cookieName));

	let isNewUser = true;
	let currentVariant;

	// Check if we can find the cookie with a valid value
	if (currentCookie) {
		for (const variant of variants) {
			if (currentCookie === variant.value) {
				isNewUser = false;
				currentVariant = variant;
			}
		}
	}

	// If we couldn't find the cookie, create a new one with generated value
	if (!currentVariant) {
		if (typeof percentageOfUsers === 'number' && variants.length === 2) {
			const randomId =
				Math.floor(Math.random() * (10 / (percentageOfUsers / 10))) === 0;
			currentVariant = randomId ? variants[0] : variants[1];
		} else {
			const randomId = Math.floor(Math.random() * variants.length);
			currentVariant = variants[randomId];
		}

		const params = {};
		if (expires && expires > 0) {
			params.expires = expires;
		}

		Cookie.set(cookieName, String(currentVariant.value), params);
	}

	// Push data layer
	const dataLayerVar = config.dataLayerVar || name;
	const dataLayerObj = {};
	dataLayerObj[dataLayerVar] = currentVariant.value;
	dataLayer.push(dataLayerObj);

	// Add optional css class to html element
	if (currentVariant.cssClass) {
		document.documentElement.classList.add(currentVariant.cssClass);
	}

	const result = {
		name: name,
		variant: currentVariant,
		isNewUser: isNewUser,
	};

	results[name] = result;

	return result;
}

export const setAbVariant = (testName, variant, expires) => {
	const cookieName = `abTest_${testName}`;
	const params = {};
	if (expires && expires > 0) {
		params.expires = expires;
	}
	Cookie.set(cookieName, String(variant), params);
};

export const tests = function () {
	const isAdmin =
		location.hostname === 'intersport-admin.stage.kes.crasman.cloud' ||
		location.hostname === 'vcap.me'; // Could use __DEV__ instead?

	// // INTERCONT-4412 Testing display of size selection on product page
	// Not in use anymore, but kept for future reference. You may use this as a template for new tests.
	// test(
	// 	{
	// 		productPageSizeSelection: [
	// 			{
	// 				value: 'new',
	// 				cssClass: 'product-page-size-selection--new',
	// 			},
	// 			{
	// 				value: 'old',
	// 				cssClass: '',
	// 			},
	// 		],
	// 		dataLayerVar: 'productPageSizeSelection',
	// 	},
	// 	isAdmin ? 100 : 50,
	// 	7
	// );
};

export { results };

// NOTE: doesn't work id called before tests() (results = {})
export const getCurrentVariant = (testName) => {
	if (results[testName]) {
		return results[testName].variant.value;
	}
	return null;
};
